import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal, ViewEncapsulation } from '@angular/core';
import { ButtonComponent, ButtonDisplayStyle, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { PAGE_COMPONENTS, PageHeaderSize, PageMode } from '@workbuddy/ui/blocks/page';
import { commandKeymap, humanizeSemanticId, SemanticCommand, ShortcutScope, useShortcuts, useShortcutScope } from '@workbuddy/ui/services/keyboard.shortcuts';
import { ActionBarComponent } from '@workbuddy/ui/v2/components/action.bar';
import type { FieldError, FieldMode } from '../../core/contracts/field.binding';
import { SURFACE_DIALOG_CHROME } from '../../core/contracts/surface.dialog.chrome';
import type { SurfaceContextHandle } from '../../core/runtime/surface.context/create.surface.context.options';
import { WbPanelComponent } from '../../core/panel/wb.panel.component';
import { WbSectionComponent } from '../../core/sections/wb.section.component';
import { resolveSurfaceActions, toSurfaceActionCommands } from '../../core/surface/surface.action.presenter';
import { buildSectionRenderMap, layoutSurfaceSections, visibleSectionsForPaths } from '../../core/surface/surface.section.presenter';
import { creatorStepBinds, creatorStepCaption, creatorStepHeader, creatorStepNavigation, creatorStepSectionPaths, currentCreatorStepIndex, revealCreatorStepErrors, visibleCreatorSteps } from '../../core/surface/creator.steps';
import { mountCreatorHostTheme } from './wb.surface.creator.host.theme';

const creatorShortcutScope: ShortcutScope = { kind: 'panel', panelId: 'surface-creator' };

/**
 * SurfaceType.Creator host: identity rail + body columns, Cancel and Create.
 *
 * A schema with steps (`.step(...)`) renders one step at a time: a step row under the title, only that
 * step's sections, "Step N of M" plus Back / Next in the footer and Create on the last visible step. Submit is still
 * one submit of the whole model. With fewer than two visible steps it renders as a single page.
 * Owns the delegating dialog's title bar (`SURFACE_DIALOG_CHROME`) so the step row sits under it.
 */
@Component({
  selector: 'wb-surface-creator-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgTemplateOutlet, PAGE_COMPONENTS, ButtonComponent, ActionBarComponent, WbPanelComponent, WbSectionComponent],
  templateUrl: './wb.surface.creator.host.component.html',
  styleUrls: ['./wb.surface.creator.host.component.less'],
  host: {
    '[attr.aria-busy]': '$busy()',
    '[attr.data-surface]': '"creator"',
    '[attr.data-section-border]': '$sectionBorder() ? "true" : "false"',
    '[attr.data-column-divider]': '$columnDivider() ? "true" : "false"',
  },
})
export class WbSurfaceCreatorHostComponent {

  private readonly document = inject(DOCUMENT);
  // Per-mount value: null when nested or when no delegating dialog frame is around this Creator.
  private readonly dialogChrome = inject(SURFACE_DIALOG_CHROME, { optional: true });

  protected readonly PageMode = PageMode;
  protected readonly PageHeaderSize = PageHeaderSize;
  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonSize = ButtonSize;

  public readonly $handle = input.required<SurfaceContextHandle>({ alias: 'handle' });

  public constructor() {

    mountCreatorHostTheme(this.document);
    this.bindSubmitShortcut();
  }

  protected readonly $submitting = signal(false);
  private readonly $submitErrors = signal<readonly FieldError[]>([]);
  /** The step the user is on, by id; null until they first move. Hidden-step fallback is the policy's. */
  private readonly $currentStepId = signal<string | null>(null);

  protected readonly $page = computed(() => this.$handle().page);
  protected readonly $contentMaxWidth = computed(() => {
    const maxWidth = this.$handle().sectionLayout.maxWidth;
    return maxWidth === null ? 'none' : `${maxWidth}px`;
  });
  protected readonly $sectionBorder = computed(() => this.$handle().sectionLayout.sectionBorder);
  protected readonly $columnDivider = computed(() => this.$handle().sectionLayout.columnDivider);
  protected readonly $surfaceMode = computed<FieldMode>(() => this.$handle().surface.defaultFieldMode);
  // Creator fields use defaultFieldMode `edit` (they are writable). Chrome is the operation: this host is create-only.
  protected readonly $title = computed(() => `New ${this.$handle().compiled.entity ?? 'Record'}`);
  protected readonly $submitLabel = computed(() => 'Create');
  protected readonly $busy = computed(() => this.$page().$initializing() || this.$submitting() || this.$page().$saving());
  protected readonly $createDisabled = computed(() => this.$busy() || this.$page().$invalid() || this.$page().$validating());
  protected readonly $errors = computed<readonly FieldError[]>(() => [
    ...this.$page().$surfaceErrors().map(error => ({ code: error.code, message: error.message })),
    ...this.$submitErrors(),
  ]);

  protected readonly $showDialogHeader = computed(() => this.dialogChrome?.$active() === true);
  protected readonly $dialogTitle = computed(() => this.dialogChrome?.$title() ?? '');

  private readonly $visibleSteps = computed(() => {
    const handle = this.$handle();
    return visibleCreatorSteps(handle.compiled.steps, bind => handle.readConditionValue(bind));
  });
  private readonly $stepIndex = computed(() => currentCreatorStepIndex(this.$handle().compiled.steps ?? [], this.$visibleSteps(), this.$currentStepId()));
  protected readonly $navigation = computed(() => creatorStepNavigation(this.$visibleSteps(), this.$stepIndex()));
  protected readonly $stepHeader = computed(() => creatorStepHeader(this.$handle().compiled.steps ?? [], this.$visibleSteps(), this.$stepIndex()));
  protected readonly $stepCaption = computed(() => {
    const handle = this.$handle();
    return creatorStepCaption(this.$visibleSteps(), this.$stepIndex(), bind => handle.readConditionValue(bind));
  });
  private readonly $currentStep = computed(() => this.$navigation().stepped ? this.$visibleSteps()[this.$stepIndex()] : null);
  /**
   * Sections on screen: the current step's when stepped, every visible step's when only one is visible
   * (a skipped step's sections stay off screen), and null — every section — for a schema with no steps.
   */
  private readonly $stepSectionPaths = computed(() => {
    const step = this.$currentStep();
    if (step) return new Set(step.sections);
    if (!this.$handle().compiled.steps) return null;
    return new Set(this.$visibleSteps().flatMap(visible => visible.sections));
  });
  protected readonly $showNext = computed(() => this.$navigation().stepped && !this.$navigation().last);

  private readonly $sectionRenderInfoByPath = computed(() => {
    return buildSectionRenderMap(this.$handle());
  });

  protected readonly $identitySections = computed(() => visibleSectionsForPaths(this.inCurrentStep(this.$handle().compiled.namedSlotSections['identity'] ?? []), this.$sectionRenderInfoByPath(), this.$page()));
  protected readonly $bodySections = computed(() => visibleSectionsForPaths(this.inCurrentStep(this.$handle().compiled.bodySections), this.$sectionRenderInfoByPath(), this.$page()));
  protected readonly $bodyLayout = computed(() => layoutSurfaceSections(this.$bodySections(), this.$handle().sectionLayout.columns, this.$handle().compiled.sectionColumns?.['body']));

  private readonly $resolvedCommands = computed(() => resolveSurfaceActions(this.$handle()));

  protected readonly $actionCommands = computed(() => toSurfaceActionCommands(this.$resolvedCommands(), this.$busy()));

  protected closeDialog(): void {

    void this.dialogChrome?.close();
  }

  protected cancel(): void {

    if (this.$busy()) return;
    this.$page().cancel();
  }

  protected back(): void {

    const previousId = this.$navigation().previousId;
    if (this.$busy() || !previousId) return;
    this.$currentStepId.set(previousId);
  }

  /** Validates only the current step's fields and moves on when they pass; otherwise their errors show. */
  protected next(): void {

    const step = this.$currentStep();
    const nextId = this.$navigation().nextId;
    if (this.$busy() || !step || !nextId) return;
    const compiled = this.$handle().compiled;
    if (!revealCreatorStepErrors(this.$page(), creatorStepBinds(compiled, step), creatorStepSectionPaths(compiled, step))) return;
    this.$currentStepId.set(nextId);
  }

  protected async create(): Promise<void> {

    if (this.$createDisabled()) return;
    this.$submitting.set(true);
    this.$submitErrors.set([]);
    try {
      const result = await this.$page().submit();
      if (!result.ok) this.$submitErrors.set(result.errors ?? []);
    } finally {
      this.$submitting.set(false);
    }
  }

  private inCurrentStep(paths: readonly string[]): readonly string[] {

    const stepPaths = this.$stepSectionPaths();
    return stepPaths ? paths.filter(path => stepPaths.has(path)) : paths;
  }

  /** `⌘↩` creates from the last step and acts as Next on every earlier one. Fires from inside a field. */
  private bindSubmitShortcut(): void {

    useShortcutScope(creatorShortcutScope);
    useShortcuts([{
      id: `creator.${SemanticCommand.Submit}`,
      keys: commandKeymap[SemanticCommand.Submit],
      label: humanizeSemanticId(SemanticCommand.Submit),
      scope: creatorShortcutScope,
      allowInInputs: true,
      when: () => !this.$busy(),
      action: () => this.$showNext() ? this.next() : void this.create(),
    }]);
  }
}
